// src/lib/resumeCalculatorDraft.js
//
// Shared "resume an unpublished calculator draft" action.
//
// Extracted from IntelligencePage.js's own handleResumeDraft (its logic is
// unchanged here, just lifted out) so the new portal start page
// (StartPage.js) can resume a draft the exact same way instead of growing
// a second, slightly-different implementation. Both callers now import
// this rather than each rolling their own fetch-then-navigate.
//
// Fetches the draft FRESH at call time (not from any already-loaded list/
// hook state) — a row that was published or soft-deleted in another tab
// since the caller's data last loaded must not be resumable as if it were
// still an editable draft. The extra owner_user_id/published_at/deleted_at
// filters below are what make that check happen at the DB level rather
// than trusting the caller's cached copy.
//
// draft_publish_framework.md invariants this respects:
//   - invariant 4: public_token is read-only, never a mutation credential
//     (irrelevant here — a draft never has a usable token in the first
//     place, so there's nothing to accidentally treat as one).
//   - invariant 5: the internal row `id` never appears in a URL. It's
//     passed only via react-router's `state`, never a query param or path
//     segment — same as the original IntelligencePage.js implementation.
//
// Always navigates to /calculator, regardless of the draft's is_autonomo
// flag. Per calculators_reference.md, both calculator journeys write the
// same CalculatorSession shape through the same calculate-irpf function —
// is_autonomo is a session-level fact, not a different product — and
// CalculatorPage.js's restore effect already rehydrates either kind of
// draft correctly. This mirrors IntelligencePage.js's original behavior,
// which also always resumed to /calculator regardless of is_autonomo, so
// nothing about the resume target changes.

import { supabase } from './supabase'

export async function resumeCalculatorDraft(navigate, userId, sessionId) {
  const { data, error } = await supabase
    .from('calculator_sessions')
    .select('id, session_payload, estimate_result, autonomo_result, filing_comparison, autonomo_irpf_impact')
    .eq('id', sessionId)
    .eq('owner_user_id', userId)
    .is('published_at', null)
    .is('deleted_at', null)
    .maybeSingle()

  if (error || !data) {
    console.error('resume draft: not found or no longer a draft', error)
    return false
  }

  navigate('/calculator', {
    state: {
      restoreSession:            data.session_payload,
      restoreSessionId:          data.id,
      restoreResult:             data.estimate_result,
      restoreAutonomoResult:     data.autonomo_result,
      restoreFilingComparison:   data.filing_comparison,
      restoreAutonomoIrpfImpact: data.autonomo_irpf_impact,
    },
  })
  return true
}
